import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { BASE_SHAREPOINT_URL } from '../components/constants'
import { authHeader } from '../utils/authHeaders';

// ── Connection Thunks ────────────────────────────────────────────────────────

export const fetchSharePointConnection = createAsyncThunk(
  'sharepoint/fetchConnection',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/connection/${orgId}`, {
        headers: { ...authHeader() }
      })
      return response.data.connection
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch connection')
    }
  }
)

export const createSharePointConnection = createAsyncThunk(
  'sharepoint/createConnection',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/connection`, data, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create connection')
    }
  }
)

export const updateSharePointConnection = createAsyncThunk(
  'sharepoint/updateConnection',
  async ({ orgId, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_SHAREPOINT_URL}/connection/${orgId}`, data, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update connection')
    }
  }
)

export const deleteSharePointConnection = createAsyncThunk(
  'sharepoint/deleteConnection',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_SHAREPOINT_URL}/connection/${orgId}`, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete connection')
    }
  }
)

// ── Configured Site Management Thunks ────────────────────────────────────────

export const fetchConfiguredSites = createAsyncThunk(
  'sharepoint/fetchConfiguredSites',
  // Accepts either a plain orgId string (org-admin Manage Organization page —
  // sees every connected site) or { orgId, taskId } (agent Files tab — sites
  // this task has hidden via "Remove site from agent" are excluded server-side).
  async (payload, { rejectWithValue }) => {
    const orgId = typeof payload === 'string' ? payload : payload?.orgId
    const taskId = typeof payload === 'string' ? undefined : payload?.taskId
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/sites/${orgId}`, {
        params: taskId ? { task_id: taskId } : {},
        headers: { ...authHeader() }
      })
      return response.data.sites
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch configured sites')
    }
  }
)

export const hideSharePointSiteForTask = createAsyncThunk(
  'sharepoint/hideSiteForTask',
  async ({ orgId, siteId, taskId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}/hide-for-task/${taskId}`, {}, {
        headers: { ...authHeader() }
      })
      return { siteId, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail)
    }
  }
)

export const fetchSitesAvailableForTask = createAsyncThunk(
  'sharepoint/fetchSitesAvailableForTask',
  async ({ orgId, taskId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/sites/${orgId}/available-for-task/${taskId}`, {
        headers: { ...authHeader() }
      })
      return response.data.sites
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to load available sites')
    }
  }
)

export const assignSiteToTask = createAsyncThunk(
  'sharepoint/assignSiteToTask',
  async ({ orgId, siteId, taskId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}/assign-to-task/${taskId}`, {}, {
        headers: { ...authHeader() }
      })
      return { siteId, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to add site')
    }
  }
)

export const fetchSiteSources = createAsyncThunk(
  'sharepoint/fetchSiteSources',
  async ({ orgId, siteId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}/sources`, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to load sources')
    }
  }
)

export const fetchTaskSources = createAsyncThunk(
  'sharepoint/fetchTaskSources',
  async ({ orgId, siteId, taskId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}/task-sources/${taskId}`, {
        headers: { ...authHeader() }
      })
      return response.data.sources
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to load selected sources')
    }
  }
)

export const fetchImportedFiles = createAsyncThunk(
  'sharepoint/fetchImportedFiles',
  async ({ orgId, taskId, siteId, path = '/' }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/imported-files/${orgId}/${taskId}/${siteId}`, {
        params: { path },
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to load imported files')
    }
  }
)

export const saveTaskSources = createAsyncThunk(
  'sharepoint/saveTaskSources',
  async ({ orgId, siteId, taskId, sources }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}/task-sources/${taskId}`, { sources }, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to save sources')
    }
  }
)

export const checkSharePointSiteAccess = createAsyncThunk(
  'sharepoint/checkSiteAccess',
  async ({ orgId, siteUrl }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sites/${orgId}/check`, {
        site_url: siteUrl,
      }, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to check site access')
    }
  }
)

export const addConfiguredSite = createAsyncThunk(
  'sharepoint/addConfiguredSite',
  async ({ orgId, siteUrl, siteName, knowledgeAreaId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sites/${orgId}`, {
        site_url: siteUrl,
        site_name: siteName || null,
        knowledge_area_id: knowledgeAreaId || null,
      }, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to add site')
    }
  }
)

export const checkExistingSiteAccess = createAsyncThunk(
  'sharepoint/checkExistingSiteAccess',
  async ({ orgId, siteId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}/check-access`, {}, {
        headers: { ...authHeader() }
      })
      return { siteId, ...response.data }
    } catch (error) {
      return rejectWithValue({ siteId, detail: error.response?.data?.detail || 'Failed to check access' })
    }
  }
)

export const fetchKnowledgeAreas = createAsyncThunk(
  'sharepoint/fetchKnowledgeAreas',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/knowledge-areas/${orgId}`, {
        headers: { ...authHeader() }
      })
      return response.data.knowledge_areas
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch knowledge areas')
    }
  }
)

export const createKnowledgeArea = createAsyncThunk(
  'sharepoint/createKnowledgeArea',
  async ({ orgId, name }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/knowledge-areas/${orgId}`, { name }, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to create knowledge area')
    }
  }
)

export const deleteKnowledgeArea = createAsyncThunk(
  'sharepoint/deleteKnowledgeArea',
  async ({ orgId, id }, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_SHAREPOINT_URL}/knowledge-areas/${orgId}/${id}`, {
        headers: { ...authHeader() }
      })
      return { id }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to delete knowledge area')
    }
  }
)

export const removeConfiguredSite = createAsyncThunk(
  'sharepoint/removeConfiguredSite',
  async ({ orgId, siteId }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}`, {
        headers: { ...authHeader() }
      })
      return { siteId, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to remove site')
    }
  }
)

export const updateConfiguredSite = createAsyncThunk(
  'sharepoint/updateConfiguredSite',
  async ({ orgId, siteId, syncIntervalMinutes, knowledgeAreaId, siteName }, { rejectWithValue }) => {
    try {
      const body = {}
      if (syncIntervalMinutes !== undefined) body.sync_interval_minutes = syncIntervalMinutes
      // '' (not omitted) tells the backend to explicitly clear the Knowledge
      // Area — distinct from the field being absent, which must leave it untouched.
      if (knowledgeAreaId !== undefined) body.knowledge_area_id = knowledgeAreaId || ''
      if (siteName !== undefined) body.site_name = siteName
      const response = await axios.put(`${BASE_SHAREPOINT_URL}/sites/${orgId}/${siteId}`, body, {
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update site')
    }
  }
)

// ── Browse Thunk ─────────────────────────────────────────────────────────────

export const browseSharePointFiles = createAsyncThunk(
  'sharepoint/browseFiles',
  async ({ orgId, siteId, path = '/', taskId }, { rejectWithValue }) => {
    try {
      const params = { site_id: siteId, path }
      if (taskId) params.task_id = taskId
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/browse/${orgId}`, {
        params,
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to browse files')
    }
  }
)

// ── Sync Thunks ──────────────────────────────────────────────────────────────

export const syncSharePointFiles = createAsyncThunk(
  'sharepoint/syncFiles',
  async ({ org_id, task_id, site_id, items, watch_deletes }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sync`, {
        org_id, task_id, site_id, items, watch_deletes
      }, {
        headers: { ...authHeader() },
        timeout: 300000  // 5 minutes for large imports
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to sync files')
    }
  }
)

export const fetchSharePointMappings = createAsyncThunk(
  'sharepoint/fetchMappings',
  async ({ orgId, taskId }, { rejectWithValue }) => {
    try {
      const params = taskId ? { task_id: taskId } : {}
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/mappings/${orgId}`, {
        params,
        headers: { ...authHeader() }
      })
      return response.data.mappings
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch mappings')
    }
  }
)

// ── Delta Sync Trigger ──────────────────────────────────────────────────────

export const triggerDeltaSync = createAsyncThunk(
  'sharepoint/triggerDeltaSync',
  async ({ orgId, siteId, resetDelta = false }, { rejectWithValue }) => {
    try {
      let url = `${BASE_SHAREPOINT_URL}/sync/trigger/${orgId}`
      const params = []
      if (siteId) params.push(`site_id=${siteId}`)
      if (resetDelta) params.push(`reset_delta=true`)
      if (params.length > 0) url += '?' + params.join('&')

      const response = await axios.post(url, {}, {
        headers: { ...authHeader() },
        timeout: 300000  // 5 minutes for large syncs
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to trigger sync')
    }
  }
)

// ── Auto-Sync Folder Thunks ──────────────────────────────────────────────────

export const fetchAutoSyncFolders = createAsyncThunk(
  'sharepoint/fetchAutoSyncFolders',
  async ({ orgId, taskId, siteId }, { rejectWithValue }) => {
    try {
      const params = {}
      if (taskId) params.task_id = taskId
      if (siteId) params.site_id = siteId
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/auto-sync-folders/${orgId}`, {
        params,
        headers: { ...authHeader() }
      })
      return response.data.folders
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch auto-sync folders')
    }
  }
)

export const addAutoSyncFolder = createAsyncThunk(
  'sharepoint/addAutoSyncFolder',
  async ({ siteId, taskId, folderPath, folderItemId, includeSubfolders = true, importExisting = true }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/auto-sync-folders`, {
        site_id: siteId,
        task_id: taskId,
        folder_path: folderPath,
        folder_item_id: folderItemId || null,
        include_subfolders: includeSubfolders,
        import_existing: importExisting,
      }, {
        headers: { ...authHeader() },
        timeout: 300000  // 5 minutes for large imports
      })
      return { ...response.data, siteId, taskId, folderPath, includeSubfolders }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to add auto-sync folder')
    }
  }
)

export const updateAutoSyncFolder = createAsyncThunk(
  'sharepoint/updateAutoSyncFolder',
  async ({ folderId, includeSubfolders }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_SHAREPOINT_URL}/auto-sync-folders/${folderId}`, {
        include_subfolders: includeSubfolders,
      }, {
        headers: { ...authHeader() }
      })
      return { folderId, includeSubfolders, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to update auto-sync folder')
    }
  }
)

export const removeAutoSyncFolder = createAsyncThunk(
  'sharepoint/removeAutoSyncFolder',
  async ({ folderId, deleteFiles = false }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_SHAREPOINT_URL}/auto-sync-folders/${folderId}`, {
        params: { delete_files: deleteFiles },
        headers: { ...authHeader() }
      })
      return { folderId, deleteFiles, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to remove auto-sync folder')
    }
  }
)

export const fetchAutoSyncFolderFilesCount = createAsyncThunk(
  'sharepoint/fetchAutoSyncFolderFilesCount',
  async (folderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/auto-sync-folders/${folderId}/files-count`, {
        headers: { ...authHeader() }
      })
      return { folderId, ...response.data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch files count')
    }
  }
)

// ── Audit Log Thunks ─────────────────────────────────────────────────────────

export const fetchAuditLogs = createAsyncThunk(
  'sharepoint/fetchAuditLogs',
  async ({ taskId, limit = 100, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_SHAREPOINT_URL}/audit-log/${taskId}`, {
        params: { limit, offset },
        headers: { ...authHeader() }
      })
      return response.data.logs
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch audit logs')
    }
  }
)

// ── Sync All / Un-sync All Thunks ────────────────────────────────────────────

export const syncAllSharePointFiles = createAsyncThunk(
  'sharepoint/syncAll',
  async ({ org_id, task_id, site_id }, { rejectWithValue, dispatch }) => {
    try {
      const startResp = await axios.post(`${BASE_SHAREPOINT_URL}/sync-all`, {
        org_id, task_id, site_id
      }, {
        headers: { ...authHeader() },
      })
      const { job_id } = startResp.data

      while (true) {
        await new Promise(r => setTimeout(r, 2000))
        const poll = await axios.get(`${BASE_SHAREPOINT_URL}/sync-all/status/${job_id}`, {
          headers: { ...authHeader() },
        })
        const job = poll.data
        dispatch(setSyncAllProgress({ total: job.total, processed: job.processed, current_file: job.current_file }))
        if (job.status === 'done') return job
        if (job.status === 'error') return rejectWithValue(job.error || 'Sync All failed')
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to sync all files')
    }
  }
)

export const unsyncAllSharePointFiles = createAsyncThunk(
  'sharepoint/unsyncAll',
  async ({ org_id, task_id, site_id }, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_SHAREPOINT_URL}/unsync-all`, {
        params: { org_id, task_id, site_id },
        headers: { ...authHeader() }
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to un-sync all files')
    }
  }
)

// ── Static (no-monitor) Sync All Thunk ───────────────────────────────────────

// Shared by syncAllStaticFiles and resumeStaticSync - tolerates a long
// stretch of consecutive poll failures instead of rejecting on the first
// few. A large job (many files, or one huge file) can legitimately run for
// tens of minutes, during which a transient blip (a worker briefly busy on
// CPU-bound OCR work, a network hiccup) can fail several polls in a row
// without the job itself having died — the job keeps running server-side
// the whole time. Giving up too early used to reject the thunk, which
// cleared all sync UI state and made the progress indicator vanish even
// though the import was still in progress. Now: a short run of failures
// only flips a "reconnecting" flag (job keeps being polled, indicator stays
// visible); only a genuinely sustained outage (SOFT * a further margin)
// gives up for real.
async function pollStaticSyncJob(job_id, dispatch, rejectWithValue) {
  dispatch(setStaticSyncJobId(job_id))
  let consecutiveFailures = 0
  const SOFT_FAILURE_THRESHOLD = 5    // ~10s: show "reconnecting", keep polling
  const HARD_FAILURE_THRESHOLD = 30   // ~60s: genuinely give up
  let connectionLost = false
  while (true) {
    await new Promise(r => setTimeout(r, 2000))
    let job
    try {
      const poll = await axios.get(`${BASE_SHAREPOINT_URL}/sync-all-static/status/${job_id}`, {
        headers: { ...authHeader() },
      })
      job = poll.data
      consecutiveFailures = 0
      if (connectionLost) {
        connectionLost = false
        dispatch(setStaticSyncConnectionLost(false))
      }
    } catch (pollError) {
      consecutiveFailures += 1
      if (consecutiveFailures >= HARD_FAILURE_THRESHOLD) {
        return rejectWithValue(pollError.response?.data?.detail || 'Lost connection to sync job')
      }
      if (consecutiveFailures >= SOFT_FAILURE_THRESHOLD && !connectionLost) {
        connectionLost = true
        dispatch(setStaticSyncConnectionLost(true))
      }
      continue
    }
    dispatch(setStaticSyncProgress({
      total: job.total,
      processed: job.processed,
      current_file: job.current_file,
    }))
    if (job.status === 'done') return job
    if (job.status === 'error') return rejectWithValue(job.error || 'Sync failed')
  }
}

export const syncAllStaticFiles = createAsyncThunk(
  'sharepoint/syncAllStatic',
  async ({ org_id, task_id, site_id, file_types, max_pages, force_reimport, watch_deletes }, { rejectWithValue, dispatch }) => {
    try {
      const startResp = await axios.post(`${BASE_SHAREPOINT_URL}/sync-all-static`, {
        org_id, task_id, site_id, file_types, max_pages, force_reimport, watch_deletes
      }, {
        headers: { ...authHeader() },
      })
      const { job_id } = startResp.data
      return await pollStaticSyncJob(job_id, dispatch, rejectWithValue)
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to start sync')
    }
  }
)

// Resumes polling a job already running server-side (discovered via
// site-stats' active_job_id), skipping the POST /sync-all-static.
export const resumeStaticSync = createAsyncThunk(
  'sharepoint/resumeStaticSync',
  async ({ job_id }, { rejectWithValue, dispatch }) => {
    return await pollStaticSyncJob(job_id, dispatch, rejectWithValue)
  }
)

// Requests cancellation of a running Sync-All-Static job. action is 'stop'
// (keep files already imported this run) or 'discard' (roll back the run).
// Does not poll itself — the syncAllStaticFiles/resumeStaticSync poll loop
// that's already running picks up the resulting 'done' status on its own
// next tick, so this thunk just needs to fire the request.
export const cancelStaticSync = createAsyncThunk(
  'sharepoint/cancelStaticSync',
  async ({ job_id, action }, { rejectWithValue }) => {
    try {
      // timeout so a backend that never responds (route missing, server not
      // yet restarted with this endpoint, connection stuck) can't leave the
      // "Processing…" modal frozen forever — it surfaces as an error instead.
      const response = await axios.post(`${BASE_SHAREPOINT_URL}/sync-all-static/cancel/${job_id}`, { action }, {
        headers: { ...authHeader() },
        timeout: 15000,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to cancel sync')
    }
  }
)

// Retries only the 'error' entries of an existing report in place — reuses
// the same staticSync* state as syncAllStaticFiles so the site card's
// existing "import running" locks (Edit sources / Import Again / 3-dot menu)
// apply during a retry too, not just a full import.
export const retryFailedFilesInReport = createAsyncThunk(
  'sharepoint/retryFailedFilesInReport',
  async ({ report_id }, { rejectWithValue, dispatch }) => {
    try {
      const startResp = await axios.post(`${BASE_SHAREPOINT_URL}/reports/${report_id}/retry-failed`, {}, {
        headers: { ...authHeader() },
      })
      const { job_id } = startResp.data
      return await pollStaticSyncJob(job_id, dispatch, rejectWithValue)
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to retry files')
    }
  }
)

// ── Sync Reports Thunks ──────────────────────────────────────────────────────

export const fetchSyncReports = createAsyncThunk(
  'sharepoint/fetchSyncReports',
  async ({ org_id, task_id, report_type } = {}, { rejectWithValue }) => {
    try {
      const params = {}
      if (org_id) params.org_id = org_id
      if (task_id) params.task_id = task_id
      if (report_type) params.report_type = report_type
      const res = await axios.get(`${BASE_SHAREPOINT_URL}/sync-reports`, {
        params,
        headers: { ...authHeader() },
      })
      return res.data.reports
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || 'Failed to load reports')
    }
  }
)

export const fetchSyncReportDetail = createAsyncThunk(
  'sharepoint/fetchSyncReportDetail',
  async (reportId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_SHAREPOINT_URL}/sync-reports/${reportId}`, {
        headers: { ...authHeader() },
      })
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || 'Failed to load report')
    }
  }
)

export const fetchFailedEmbedFiles = createAsyncThunk(
  'sharepoint/fetchFailedEmbedFiles',
  async ({ org_id, task_id } = {}, { rejectWithValue }) => {
    try {
      const params = {}
      if (org_id) params.org_id = org_id
      if (task_id) params.task_id = task_id
      const res = await axios.get(`${BASE_SHAREPOINT_URL}/failed-embed-files`, {
        params,
        headers: { ...authHeader() },
      })
      return res.data.files
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || 'Failed to load failed files')
    }
  }
)

export const retryEmbedFile = createAsyncThunk(
  'sharepoint/retryEmbedFile',
  async (fileId, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_SHAREPOINT_URL}/retry-embed/${fileId}`, {}, {
        headers: { ...authHeader() },
      })
      return { fileId, ...res.data }
    } catch (e) {
      return rejectWithValue({ fileId, error: e.response?.data?.detail || 'Retry failed' })
    }
  }
)

// ── Slice ────────────────────────────────────────────────────────────────────

const sharepointSlice = createSlice({
  name: 'sharepoint',
  initialState: {
    connection: null,
    connectionLoading: false,
    connectionError: null,

    // Configured sites
    configuredSites: [],
    configuredSitesLoading: false,
    configuredSitesError: null,
    addSiteLoading: false,
    addSiteError: null,

    // Step 1 "Check access" of the Connect-site wizard (dry-run, no DB write)
    siteCheckLoading: false,
    siteCheckResult: null,
    siteCheckError: null,

    // "Check access now" on an already-connected site (site details panel)
    existingSiteCheckLoadingId: null,
    existingSiteCheckError: null,

    // Agent-side "+ Add site" wizard step 1 — org sites this task can add
    sitesAvailableForTask: [],
    sitesAvailableForTaskLoading: false,
    sitesAvailableForTaskError: null,
    assignSiteLoading: false,
    assignSiteError: null,

    // "+ Add site" wizard step 2 — sources (libraries/lists) picker
    siteSources: null, // { libraries: [...], lists: [...] }
    siteSourcesLoading: false,
    siteSourcesError: null,
    taskSources: [],
    taskSourcesLoading: false,
    saveSourcesLoading: false,
    saveSourcesError: null,

    // Browsing already-imported files (folder-tree view of what got imported)
    importedFilesContent: null, // { folders: [...], files: [...] }
    importedFilesLoading: false,
    importedFilesError: null,

    // Knowledge Areas — independent entity, org-scoped
    knowledgeAreas: [],
    knowledgeAreasLoading: false,
    knowledgeAreasError: null,
    createKnowledgeAreaLoading: false,
    createKnowledgeAreaError: null,
    deleteKnowledgeAreaLoading: null, // id currently being deleted

    // Selected site for browsing
    selectedSite: null,

    browseItems: [],
    browsePath: '/',
    browseLoading: false,
    browseError: null,

    syncLoading: false,
    syncResults: null,
    syncError: null,

    mappings: [],
    mappingsLoading: false,

    // Auto-sync folders
    autoSyncFolders: [],
    autoSyncFoldersLoading: false,
    autoSyncFoldersError: null,
    addAutoSyncLoading: false,
    addAutoSyncError: null,
    removeAutoSyncLoading: false,
    autoSyncFilesCount: null,
    autoSyncFilesCountLoading: false,

    // Delta sync trigger
    deltaSyncLoading: false,
    deltaSyncResults: null,
    deltaSyncError: null,

    // Audit logs
    auditLogs: [],
    auditLogsLoading: false,
    auditLogsError: null,

    // Sync All / Un-sync All
    syncAllLoading: false,
    syncAllResults: null,
    syncAllError: null,
    syncAllProgress: null,  // { total, processed, current_file }
    unsyncAllLoading: false,
    unsyncAllResults: null,
    unsyncAllError: null,

    // Static (no-monitor) Sync All
    staticSyncLoading: false,
    staticSyncResults: null,
    staticSyncError: null,
    staticSyncProgress: null,  // { total, processed, current_file }
    staticSyncPhase: 'idle',   // 'idle' | 'running' | 'done' | 'error'
    staticSyncSite: null,
    staticSyncMinimized: false,
    staticSyncResultsData: null,
    staticSyncJobId: null,
    staticSyncCancelling: false,
    staticSyncConnectionLost: false,

    // Sync Reports (SUPER_ADMIN)
    syncReportsLoading: false,
    syncReports: [],
    syncReportsError: null,
    syncReportDetail: null,
    syncReportDetailLoading: false,
    syncReportDetailError: null,

    // Failed Embed Files (admin retry)
    failedEmbedFiles: [],
    failedEmbedLoading: false,
    failedEmbedError: null,
    retryingFileId: null,
  },
  reducers: {
    clearSharePointErrors: (state) => {
      state.connectionError = null
      state.browseError = null
      state.syncError = null
      state.configuredSitesError = null
      state.addSiteError = null
      state.autoSyncFoldersError = null
      state.addAutoSyncError = null
    },
    clearSyncResults: (state) => {
      state.syncResults = null
    },
    clearBrowseItems: (state) => {
      state.browseItems = []
      state.browsePath = '/'
    },
    setSelectedSite: (state, action) => {
      state.selectedSite = action.payload
      state.browseItems = []
      state.browsePath = '/'
    },
    clearSiteSelection: (state) => {
      state.selectedSite = null
      state.browseItems = []
      state.browsePath = '/'
    },
    clearAutoSyncFolders: (state) => {
      state.autoSyncFolders = []
    },
    clearAutoSyncFilesCount: (state) => {
      state.autoSyncFilesCount = null
    },
    clearSiteCheckResult: (state) => {
      state.siteCheckResult = null
      state.siteCheckError = null
    },
    setSyncAllProgress: (state, action) => {
      state.syncAllProgress = action.payload
    },
    setStaticSyncProgress: (state, action) => {
      state.staticSyncProgress = action.payload
    },
    setStaticSyncPhase: (state, action) => {
      state.staticSyncPhase = action.payload
    },
    setStaticSyncSite: (state, action) => {
      state.staticSyncSite = action.payload
    },
    setStaticSyncMinimized: (state, action) => {
      state.staticSyncMinimized = action.payload
    },
    setStaticSyncResultsData: (state, action) => {
      state.staticSyncResultsData = action.payload
    },
    setStaticSyncJobId: (state, action) => {
      state.staticSyncJobId = action.payload
    },
    setStaticSyncConnectionLost: (state, action) => {
      state.staticSyncConnectionLost = action.payload
    },
    clearStaticSync: (state) => {
      state.staticSyncPhase = 'idle'
      state.staticSyncSite = null
      state.staticSyncMinimized = false
      state.staticSyncResultsData = null
      state.staticSyncProgress = null
      state.staticSyncJobId = null
      state.staticSyncCancelling = false
      state.staticSyncConnectionLost = false
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch connection
      .addCase(fetchSharePointConnection.pending, (state) => {
        state.connectionLoading = true
        state.connectionError = null
      })
      .addCase(fetchSharePointConnection.fulfilled, (state, action) => {
        state.connectionLoading = false
        state.connection = action.payload
      })
      .addCase(fetchSharePointConnection.rejected, (state, action) => {
        state.connectionLoading = false
        state.connectionError = action.payload
      })

      // Create connection
      .addCase(createSharePointConnection.pending, (state) => {
        state.connectionLoading = true
        state.connectionError = null
      })
      .addCase(createSharePointConnection.fulfilled, (state) => {
        state.connectionLoading = false
      })
      .addCase(createSharePointConnection.rejected, (state, action) => {
        state.connectionLoading = false
        state.connectionError = action.payload
      })

      // Update connection
      .addCase(updateSharePointConnection.pending, (state) => {
        state.connectionLoading = true
      })
      .addCase(updateSharePointConnection.fulfilled, (state) => {
        state.connectionLoading = false
      })
      .addCase(updateSharePointConnection.rejected, (state, action) => {
        state.connectionLoading = false
        state.connectionError = action.payload
      })

      // Delete connection
      .addCase(deleteSharePointConnection.fulfilled, (state) => {
        state.connection = null
        state.configuredSites = []
      })

      // Fetch configured sites
      .addCase(fetchConfiguredSites.pending, (state) => {
        state.configuredSitesLoading = true
        state.configuredSitesError = null
      })
      .addCase(fetchConfiguredSites.fulfilled, (state, action) => {
        state.configuredSitesLoading = false
        state.configuredSites = action.payload || []
      })
      .addCase(fetchConfiguredSites.rejected, (state, action) => {
        state.configuredSitesLoading = false
        state.configuredSitesError = action.payload
      })

      // Check site access (Step 1 dry-run)
      .addCase(checkSharePointSiteAccess.pending, (state) => {
        state.siteCheckLoading = true
        state.siteCheckError = null
        state.siteCheckResult = null
      })
      .addCase(checkSharePointSiteAccess.fulfilled, (state, action) => {
        state.siteCheckLoading = false
        state.siteCheckResult = action.payload
      })
      .addCase(checkSharePointSiteAccess.rejected, (state, action) => {
        state.siteCheckLoading = false
        state.siteCheckError = action.payload
      })

      // "+ Add site" wizard step 1 — sites available to this task
      .addCase(fetchSitesAvailableForTask.pending, (state) => {
        state.sitesAvailableForTaskLoading = true
        state.sitesAvailableForTaskError = null
      })
      .addCase(fetchSitesAvailableForTask.fulfilled, (state, action) => {
        state.sitesAvailableForTaskLoading = false
        state.sitesAvailableForTask = action.payload || []
      })
      .addCase(fetchSitesAvailableForTask.rejected, (state, action) => {
        state.sitesAvailableForTaskLoading = false
        state.sitesAvailableForTaskError = action.payload
      })

      .addCase(assignSiteToTask.pending, (state) => {
        state.assignSiteLoading = true
        state.assignSiteError = null
      })
      .addCase(assignSiteToTask.fulfilled, (state) => {
        state.assignSiteLoading = false
      })
      .addCase(assignSiteToTask.rejected, (state, action) => {
        state.assignSiteLoading = false
        state.assignSiteError = action.payload
      })

      // "+ Add site" wizard step 2 — live libraries/lists for a site
      .addCase(fetchSiteSources.pending, (state) => {
        state.siteSourcesLoading = true
        state.siteSourcesError = null
        state.siteSources = null
      })
      .addCase(fetchSiteSources.fulfilled, (state, action) => {
        state.siteSourcesLoading = false
        state.siteSources = action.payload
      })
      .addCase(fetchSiteSources.rejected, (state, action) => {
        state.siteSourcesLoading = false
        state.siteSourcesError = action.payload
      })

      .addCase(fetchTaskSources.pending, (state) => {
        state.taskSourcesLoading = true
      })
      .addCase(fetchTaskSources.fulfilled, (state, action) => {
        state.taskSourcesLoading = false
        state.taskSources = action.payload || []
      })
      .addCase(fetchTaskSources.rejected, (state) => {
        state.taskSourcesLoading = false
        state.taskSources = []
      })

      .addCase(fetchImportedFiles.pending, (state) => {
        state.importedFilesLoading = true
        state.importedFilesError = null
      })
      .addCase(fetchImportedFiles.fulfilled, (state, action) => {
        state.importedFilesLoading = false
        state.importedFilesContent = action.payload
      })
      .addCase(fetchImportedFiles.rejected, (state, action) => {
        state.importedFilesLoading = false
        state.importedFilesError = action.payload
      })

      .addCase(saveTaskSources.pending, (state) => {
        state.saveSourcesLoading = true
        state.saveSourcesError = null
      })
      .addCase(saveTaskSources.fulfilled, (state) => {
        state.saveSourcesLoading = false
      })
      .addCase(saveTaskSources.rejected, (state, action) => {
        state.saveSourcesLoading = false
        state.saveSourcesError = action.payload
      })

      // Knowledge Areas
      .addCase(fetchKnowledgeAreas.pending, (state) => {
        state.knowledgeAreasLoading = true
        state.knowledgeAreasError = null
      })
      .addCase(fetchKnowledgeAreas.fulfilled, (state, action) => {
        state.knowledgeAreasLoading = false
        state.knowledgeAreas = action.payload || []
      })
      .addCase(fetchKnowledgeAreas.rejected, (state, action) => {
        state.knowledgeAreasLoading = false
        state.knowledgeAreasError = action.payload
      })
      .addCase(createKnowledgeArea.pending, (state) => {
        state.createKnowledgeAreaLoading = true
        state.createKnowledgeAreaError = null
      })
      .addCase(createKnowledgeArea.fulfilled, (state, action) => {
        state.createKnowledgeAreaLoading = false
        state.knowledgeAreas = [...state.knowledgeAreas, action.payload].sort((a, b) => a.name.localeCompare(b.name))
      })
      .addCase(createKnowledgeArea.rejected, (state, action) => {
        state.createKnowledgeAreaLoading = false
        state.createKnowledgeAreaError = action.payload
      })
      .addCase(deleteKnowledgeArea.pending, (state, action) => {
        state.deleteKnowledgeAreaLoading = action.meta.arg.id
      })
      .addCase(deleteKnowledgeArea.fulfilled, (state, action) => {
        state.deleteKnowledgeAreaLoading = null
        state.knowledgeAreas = state.knowledgeAreas.filter(k => k.id !== action.payload.id)
      })
      .addCase(deleteKnowledgeArea.rejected, (state) => {
        state.deleteKnowledgeAreaLoading = null
      })

      // Add configured site
      .addCase(addConfiguredSite.pending, (state) => {
        state.addSiteLoading = true
        state.addSiteError = null
      })
      .addCase(addConfiguredSite.fulfilled, (state) => {
        state.addSiteLoading = false
      })
      .addCase(addConfiguredSite.rejected, (state, action) => {
        state.addSiteLoading = false
        state.addSiteError = action.payload
      })

      // Remove configured site
      .addCase(removeConfiguredSite.fulfilled, (state, action) => {
        state.configuredSites = state.configuredSites.filter(s => s.id !== action.payload.siteId)
      })

      // Update configured site
      .addCase(updateConfiguredSite.fulfilled, (state, action) => {
        const index = state.configuredSites.findIndex(s => s.id === action.payload.id)
        if (index !== -1) {
          state.configuredSites[index] = action.payload
        }
      })

      // Check access now (existing site)
      .addCase(checkExistingSiteAccess.pending, (state, action) => {
        state.existingSiteCheckLoadingId = action.meta.arg.siteId
        state.existingSiteCheckError = null
      })
      .addCase(checkExistingSiteAccess.fulfilled, (state, action) => {
        state.existingSiteCheckLoadingId = null
        const index = state.configuredSites.findIndex(s => s.id === action.payload.siteId)
        if (index !== -1 && action.payload.site) {
          state.configuredSites[index] = action.payload.site
        }
      })
      .addCase(checkExistingSiteAccess.rejected, (state, action) => {
        state.existingSiteCheckLoadingId = null
        state.existingSiteCheckError = action.payload
      })

      // Browse files
      .addCase(browseSharePointFiles.pending, (state) => {
        state.browseLoading = true
        state.browseError = null
      })
      .addCase(browseSharePointFiles.fulfilled, (state, action) => {
        state.browseLoading = false
        state.browseItems = action.payload.items || []
        state.browsePath = action.payload.current_path || '/'
      })
      .addCase(browseSharePointFiles.rejected, (state, action) => {
        state.browseLoading = false
        state.browseError = action.payload
      })

      // Sync files
      .addCase(syncSharePointFiles.pending, (state) => {
        state.syncLoading = true
        state.syncError = null
        state.syncResults = null
      })
      .addCase(syncSharePointFiles.fulfilled, (state, action) => {
        state.syncLoading = false
        state.syncResults = action.payload
      })
      .addCase(syncSharePointFiles.rejected, (state, action) => {
        state.syncLoading = false
        state.syncError = action.payload
      })

      // Fetch mappings
      .addCase(fetchSharePointMappings.pending, (state) => {
        state.mappingsLoading = true
      })
      .addCase(fetchSharePointMappings.fulfilled, (state, action) => {
        state.mappingsLoading = false
        state.mappings = action.payload
      })
      .addCase(fetchSharePointMappings.rejected, (state) => {
        state.mappingsLoading = false
      })

      // Fetch auto-sync folders
      .addCase(fetchAutoSyncFolders.pending, (state) => {
        state.autoSyncFoldersLoading = true
        state.autoSyncFoldersError = null
      })
      .addCase(fetchAutoSyncFolders.fulfilled, (state, action) => {
        state.autoSyncFoldersLoading = false
        state.autoSyncFolders = action.payload || []
      })
      .addCase(fetchAutoSyncFolders.rejected, (state, action) => {
        state.autoSyncFoldersLoading = false
        state.autoSyncFoldersError = action.payload
      })

      // Add auto-sync folder
      .addCase(addAutoSyncFolder.pending, (state) => {
        state.addAutoSyncLoading = true
        state.addAutoSyncError = null
      })
      .addCase(addAutoSyncFolder.fulfilled, (state, action) => {
        state.addAutoSyncLoading = false
        // Add the new folder to the list
        state.autoSyncFolders.push({
          id: action.payload.folder_id,
          site_id: action.payload.siteId,
          task_id: action.payload.taskId,
          folder_path: action.payload.folderPath,
          include_subfolders: action.payload.includeSubfolders,
        })
      })
      .addCase(addAutoSyncFolder.rejected, (state, action) => {
        state.addAutoSyncLoading = false
        state.addAutoSyncError = action.payload
      })

      // Update auto-sync folder
      .addCase(updateAutoSyncFolder.fulfilled, (state, action) => {
        const index = state.autoSyncFolders.findIndex(f => f.id === action.payload.folderId)
        if (index !== -1) {
          state.autoSyncFolders[index].include_subfolders = action.payload.includeSubfolders
        }
      })

      // Remove auto-sync folder
      .addCase(removeAutoSyncFolder.pending, (state) => {
        state.removeAutoSyncLoading = true
      })
      .addCase(removeAutoSyncFolder.fulfilled, (state, action) => {
        state.removeAutoSyncLoading = false
        state.autoSyncFolders = state.autoSyncFolders.filter(f => f.id !== action.payload.folderId)
        state.autoSyncFilesCount = null
      })
      .addCase(removeAutoSyncFolder.rejected, (state) => {
        state.removeAutoSyncLoading = false
      })

      // Fetch auto-sync folder files count
      .addCase(fetchAutoSyncFolderFilesCount.pending, (state) => {
        state.autoSyncFilesCountLoading = true
        state.autoSyncFilesCount = null
      })
      .addCase(fetchAutoSyncFolderFilesCount.fulfilled, (state, action) => {
        state.autoSyncFilesCountLoading = false
        state.autoSyncFilesCount = action.payload
      })
      .addCase(fetchAutoSyncFolderFilesCount.rejected, (state) => {
        state.autoSyncFilesCountLoading = false
        state.autoSyncFilesCount = null
      })

      // Trigger delta sync
      .addCase(triggerDeltaSync.pending, (state) => {
        state.deltaSyncLoading = true
        state.deltaSyncError = null
        state.deltaSyncResults = null
      })
      .addCase(triggerDeltaSync.fulfilled, (state, action) => {
        state.deltaSyncLoading = false
        state.deltaSyncResults = action.payload
      })
      .addCase(triggerDeltaSync.rejected, (state, action) => {
        state.deltaSyncLoading = false
        state.deltaSyncError = action.payload
      })

      // Fetch audit logs
      .addCase(fetchAuditLogs.pending, (state) => {
        state.auditLogsLoading = true
        state.auditLogsError = null
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        state.auditLogsLoading = false
        state.auditLogs = action.payload || []
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        state.auditLogsLoading = false
        state.auditLogsError = action.payload
      })

      // Sync All
      .addCase(syncAllSharePointFiles.pending, (state) => {
        state.syncAllLoading = true
        state.syncAllError = null
        state.syncAllResults = null
      })
      .addCase(syncAllSharePointFiles.fulfilled, (state, action) => {
        state.syncAllLoading = false
        state.syncAllResults = action.payload
      })
      .addCase(syncAllSharePointFiles.rejected, (state, action) => {
        state.syncAllLoading = false
        state.syncAllError = action.payload
      })

      // Un-sync All
      .addCase(unsyncAllSharePointFiles.pending, (state) => {
        state.unsyncAllLoading = true
        state.unsyncAllError = null
        state.unsyncAllResults = null
      })
      .addCase(unsyncAllSharePointFiles.fulfilled, (state, action) => {
        state.unsyncAllLoading = false
        state.unsyncAllResults = action.payload
      })
      .addCase(unsyncAllSharePointFiles.rejected, (state, action) => {
        state.unsyncAllLoading = false
        state.unsyncAllError = action.payload
      })
      // Static Sync All
      .addCase(syncAllStaticFiles.pending, (state) => {
        state.staticSyncLoading = true
        state.staticSyncResults = null
        state.staticSyncError = null
        state.staticSyncProgress = null
        state.staticSyncPhase = 'running'
        state.staticSyncConnectionLost = false
      })
      .addCase(syncAllStaticFiles.fulfilled, (state, action) => {
        state.staticSyncLoading = false
        state.staticSyncResults = action.payload
        state.staticSyncProgress = null
        state.staticSyncPhase = 'done'
        state.staticSyncResultsData = action.payload
        state.staticSyncConnectionLost = false
      })
      .addCase(syncAllStaticFiles.rejected, (state, action) => {
        state.staticSyncLoading = false
        state.staticSyncError = action.payload
        state.staticSyncProgress = null
        state.staticSyncPhase = 'error'
        state.staticSyncConnectionLost = false
      })
      .addCase(resumeStaticSync.pending, (state) => {
        state.staticSyncLoading = true
        state.staticSyncResults = null
        state.staticSyncError = null
        state.staticSyncPhase = 'running'
        state.staticSyncConnectionLost = false
      })
      .addCase(resumeStaticSync.fulfilled, (state, action) => {
        state.staticSyncLoading = false
        state.staticSyncResults = action.payload
        state.staticSyncProgress = null
        state.staticSyncPhase = 'done'
        state.staticSyncResultsData = action.payload
        state.staticSyncConnectionLost = false
      })
      .addCase(resumeStaticSync.rejected, (state, action) => {
        state.staticSyncLoading = false
        state.staticSyncError = action.payload
        state.staticSyncProgress = null
        state.staticSyncPhase = 'error'
        state.staticSyncConnectionLost = false
      })
      // Retry Failed Files (same report, in place)
      .addCase(retryFailedFilesInReport.pending, (state) => {
        state.staticSyncLoading = true
        state.staticSyncResults = null
        state.staticSyncError = null
        state.staticSyncProgress = null
        state.staticSyncPhase = 'running'
        state.staticSyncConnectionLost = false
      })
      .addCase(retryFailedFilesInReport.fulfilled, (state, action) => {
        state.staticSyncLoading = false
        state.staticSyncResults = action.payload
        state.staticSyncProgress = null
        state.staticSyncPhase = 'done'
        state.staticSyncResultsData = action.payload
        state.staticSyncConnectionLost = false
      })
      .addCase(retryFailedFilesInReport.rejected, (state, action) => {
        state.staticSyncLoading = false
        state.staticSyncError = action.payload
        state.staticSyncProgress = null
        state.staticSyncPhase = 'error'
        state.staticSyncConnectionLost = false
      })
      // Cancel Sync-All-Static (stop / discard) — the request itself only
      // flips a DB flag; the already-running poll loop picks up 'done' on
      // its own next tick, so this just tracks the in-flight cancel request.
      .addCase(cancelStaticSync.pending, (state) => {
        state.staticSyncCancelling = true
      })
      .addCase(cancelStaticSync.fulfilled, (state) => {
        state.staticSyncCancelling = false
      })
      .addCase(cancelStaticSync.rejected, (state) => {
        state.staticSyncCancelling = false
      })
      // Sync Reports
      .addCase(fetchSyncReports.pending, (state) => {
        state.syncReportsLoading = true
        state.syncReportsError = null
      })
      .addCase(fetchSyncReports.fulfilled, (state, action) => {
        state.syncReportsLoading = false
        state.syncReports = action.payload
      })
      .addCase(fetchSyncReports.rejected, (state, action) => {
        state.syncReportsLoading = false
        state.syncReportsError = action.payload
      })
      .addCase(fetchSyncReportDetail.pending, (state) => {
        state.syncReportDetailLoading = true
        state.syncReportDetailError = null
        state.syncReportDetail = null
      })
      .addCase(fetchSyncReportDetail.fulfilled, (state, action) => {
        state.syncReportDetailLoading = false
        state.syncReportDetail = action.payload
      })
      .addCase(fetchSyncReportDetail.rejected, (state, action) => {
        state.syncReportDetailLoading = false
        state.syncReportDetailError = action.payload
      })
      .addCase(fetchFailedEmbedFiles.pending, (state) => {
        state.failedEmbedLoading = true
        state.failedEmbedError = null
      })
      .addCase(fetchFailedEmbedFiles.fulfilled, (state, action) => {
        state.failedEmbedLoading = false
        state.failedEmbedFiles = action.payload
      })
      .addCase(fetchFailedEmbedFiles.rejected, (state, action) => {
        state.failedEmbedLoading = false
        state.failedEmbedError = action.payload
      })
      .addCase(retryEmbedFile.pending, (state, action) => {
        state.retryingFileId = action.meta.arg
      })
      .addCase(retryEmbedFile.fulfilled, (state, action) => {
        state.retryingFileId = null
        const { fileId, success, retry_count } = action.payload
        const file = state.failedEmbedFiles.find(f => f.id === fileId)
        if (file) {
          file.embed_retry_count = retry_count
          if (success) {
            // Remove from failed list
            state.failedEmbedFiles = state.failedEmbedFiles.filter(f => f.id !== fileId)
          } else if (retry_count >= 3) {
            file.embed_status = 'max_retries'
          }
        }
      })
      .addCase(retryEmbedFile.rejected, (state, action) => {
        state.retryingFileId = null
        // Update retry count on the file if returned
        const fileId = action.payload?.fileId
        if (fileId) {
          const file = state.failedEmbedFiles.find(f => f.id === fileId)
          if (file) file._retryError = action.payload.error
        }
      })
  }
})

export const {
  clearSharePointErrors,
  clearSyncResults,
  clearBrowseItems,
  setSelectedSite,
  clearSiteSelection,
  clearAutoSyncFolders,
  clearAutoSyncFilesCount,
  clearSiteCheckResult,
  setSyncAllProgress,
  setStaticSyncProgress,
  setStaticSyncPhase,
  setStaticSyncSite,
  setStaticSyncMinimized,
  setStaticSyncResultsData,
  setStaticSyncJobId,
  setStaticSyncConnectionLost,
  clearStaticSync,
} = sharepointSlice.actions

export default sharepointSlice.reducer
